import { useEffect, useMemo, useState } from "react";
import { SearchResultDetailDialog } from "../search/search-result-detail-dialog";
import type { SearchResult, SourceManifest } from "../api";
import type { ReaderWorkspace } from "../reader/reader-workspace";
import { SourceSummary } from "./source-card";
import { SourceDiscoverPanel } from "./source-discover-panel";
import { createDefaultSourceGroups, createSourceGroupTabs, getSourceGroupLabels } from "./source-groups";

export function SourcesPage({ workspace }: { workspace: ReaderWorkspace }) {
  const sourceTabs = useMemo(() => createSourceGroupTabs(workspace.sources, workspace.sourceGroups), [workspace.sourceGroups, workspace.sources]);
  const [activeTabId, setActiveTabId] = useState("all");
  const [expandedSourceId, setExpandedSourceId] = useState<string | null>(null);
  const [detailResult, setDetailResult] = useState<SearchResult | null>(null);
  const activeTab = sourceTabs.find(tab => tab.id === activeTabId) ?? sourceTabs[0];
  const visibleSources = (activeTab?.sources ?? []).filter(source => workspace.enabledSourceIds.includes(source.id));

  useEffect(() => {
    const onlyEmptyAllGroup = workspace.sourceGroups.length === 1 && workspace.sourceGroups[0]?.id === "all" && workspace.sourceGroups[0].sourceIds.length === 0;
    if (workspace.sources.length === 0 || workspace.sourceGroups.length > 0 && !onlyEmptyAllGroup) return;
    void workspace.handleSaveSourceGroups(createDefaultSourceGroups(workspace.sources));
  }, [workspace.handleSaveSourceGroups, workspace.sourceGroups, workspace.sources]);

  useEffect(() => {
    if (!expandedSourceId || workspace.enabledSourceIds.includes(expandedSourceId)) return;
    setExpandedSourceId(null);
  }, [expandedSourceId, workspace.enabledSourceIds]);

  useEffect(() => {
    if (sourceTabs.some(tab => tab.id === activeTabId)) return;
    setActiveTabId("all");
  }, [activeTabId, sourceTabs]);

  useEffect(() => {
    if (!expandedSourceId || visibleSources.some(source => source.id === expandedSourceId)) return;
    setExpandedSourceId(null);
  }, [expandedSourceId, visibleSources]);

  function handleSourceClick(source: SourceManifest, enabled: boolean) {
    if (!enabled) return;

    const nextSourceId = expandedSourceId === source.id ? null : source.id;
    setExpandedSourceId(nextSourceId);

    if (nextSourceId && source.capabilities.includes("discover")) {
      void workspace.handleLoadSourceDiscover(source.id);
    }
  }

  return (
    <div className="space-y-4">
      <section className="space-y-3">
        <div className="flex min-w-0 gap-2 overflow-x-auto rounded-2xl border bg-card p-1">
          {sourceTabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
                tab.id === activeTabId ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              }`}
              onClick={() => setActiveTabId(tab.id)}
            >
              <span>{tab.label}</span>
              <span className={`rounded-full px-1.5 py-0.5 text-[11px] ${tab.id === activeTabId ? "bg-background/20" : "bg-muted text-muted-foreground"}`}>
                {tab.sources.filter(source => workspace.enabledSourceIds.includes(source.id)).length}
              </span>
            </button>
          ))}
        </div>

        <div className="divide-y overflow-hidden rounded-2xl border bg-card">
          {visibleSources.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">当前分组暂无书源</div>
          )}
          {visibleSources.map(source => {
            const enabled = workspace.enabledSourceIds.includes(source.id);
            const status = workspace.sourceStatuses.find(item => item.sourceId === source.id);
            const expanded = expandedSourceId === source.id;
            const discover = workspace.sourceDiscoverById[source.id];
            const homeDiscover = workspace.sourceHomeDiscoverById[source.id];
            const discoverLoading = workspace.discoverLoadingSourceId === source.id;

            return (
              <article key={source.id} className={enabled ? "bg-card" : "bg-card opacity-60"}>
                <div className="px-4 py-3">
                  <button
                    type="button"
                    disabled={!enabled}
                    className="-m-2 w-full min-w-0 rounded-xl p-2 text-left transition-colors hover:bg-accent/40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    onClick={() => handleSourceClick(source, enabled)}
                  >
                    <SourceSummary source={source} status={status} expanded={expanded} groupLabels={getSourceGroupLabels(source.id, sourceTabs)} />
                  </button>
                </div>

                {enabled && expanded && (
                  <SourceDiscoverPanel
                    source={source}
                    discoverCapable={source.capabilities.includes("discover")}
                    loading={discoverLoading}
                    discover={discover}
                    homeDiscover={homeDiscover}
                    onNavigate={targetUrl => void workspace.handleLoadSourceDiscover(source.id, targetUrl)}
                    onHomeSelect={() => void workspace.handleLoadSourceDiscover(source.id)}
                    onBookSelect={setDetailResult}
                  />
                )}
              </article>
            );
          })}
        </div>
      </section>

      {detailResult && (
        <SearchResultDetailDialog
          result={detailResult}
          workspace={workspace}
          onClose={() => setDetailResult(null)}
        />
      )}
    </div>
  );
}
